import { Box, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Checkbox, Chip, Divider, Stack, Typography, Card, Tooltip, TextField, IconButton } from "@mui/material";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { SyntheticEvent, useState, useMemo, useEffect } from "react";
import { ColDef } from "ag-grid-community";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData } from "@lib/apis";
import { useMutation, useQuery } from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { Delete, Pause, PlayArrow, Refresh, CloudUpload, AddLink, FactCheck, PersonRemove, Add, RemoveCircleOutline, PauseCircleOutline, PlayCircleOutline, SettingsSuggest, ScheduleSend } from "@mui/icons-material";
import { useDialogs } from "@toolpad/core";
import { useSnackbar } from "notistack";
import { alpha, useTheme } from '@mui/material';
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";

export default function AdminWarmupSystem() {
    const { enqueueSnackbar } = useSnackbar();
    const dialogs = useDialogs();    

    const [statusMap, setStatusMap] = useState<Record<string, boolean>>({});
    const [tabValue, setTabValue] = useState<number>(0);
    const [exportLoading, setExportLoading] = useState(false);

    const warmupDomainsQuery = useQuery({
        queryKey: ["warmup-domains"],
        queryFn: () => authenticateAndFetchData(`/admin/warmup/active-list/`),
    });

    const pauseDomainsQuery = useQuery({
        queryKey: ["pause-domains"],
        queryFn: () => authenticateAndFetchData(`/admin/warmup/all-pause-domains/`),
    });    

    const manageStatusMutation = useMutation({
        mutationFn: ({ domain, action }: { domain: string, action: 'pause' | 'resume' | 'unregister' }) => 
            authenticateAndPostData(`/admin/warmup/pause-resume-unregiste/?action=${action}`, { domain }),
        onSuccess: (res) => {
            enqueueSnackbar(res.data.message || "Action successful", { variant: "success" });
            warmupDomainsQuery.refetch();
            pauseDomainsQuery.refetch();
        },
        onError: (err: ApiRequestFailed) => enqueueSnackbar(err.data.message, { variant: "error" })
    });
    
    const checkActiveMutation = useMutation({
        mutationFn: (domains: string[]) => 
            authenticateAndPostData(`/admin/warmup/check-status/`, { domains }),
        onSuccess: (res) => {            
            setStatusMap(res.data);
            enqueueSnackbar("Health check complete", { variant: "success" });
        },
        onError: (err: ApiRequestFailed) => enqueueSnackbar(err.data.message, { variant: "error" })
    });

    const handleTabChange = async (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const outlookScheduleMutation = useMutation({
        mutationFn: (payload: { email: string, name: string }) => 
            authenticateAndPostData(`/admin/warmup/outlook-schedules/`, payload),
        onSuccess: () => enqueueSnackbar("Outlook schedules generated!", { variant: "success" }),
        onError: (err: ApiRequestFailed) => enqueueSnackbar(err.data.message, { variant: "error" })
    });

    const deleteAccountMutation = useMutation({
        mutationFn: ({ type, email }: { type: 'ms' | 'gmail', email: string }) => 
            authenticateAndPostData(`/admin/warmup/delete-${type}/`, { email }),
        onSuccess: () => enqueueSnackbar("Account deleted from warmup system", { variant: "success" }),
        onError: (err: ApiRequestFailed) => enqueueSnackbar(err.data.message, { variant: "error" })
    });

    
    const handleDeleteAccount = async (type: 'ms' | 'gmail') => {
        const title = type === 'gmail' ? "Gmail Delete Account" : "Delete Ms Account";
        const result = await dialogs.confirm(
            <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
                <Typography variant="body2">Enter the email address to delete from the system:</Typography>
                <TextField id="del-email" label="Email Address" fullWidth variant="outlined" />
            </Stack>,
            { title, okText: 'Delete Account' }
        );

        if (result) {
            const email = (document.getElementById('del-email') as HTMLInputElement).value;
            if (email) deleteAccountMutation.mutate({ type, email });
        }
    };

    const handleGenerateSchedules = async () => {
        const result = await dialogs.confirm(
            <Stack spacing={2.5} sx={{ mt: 1, width: 450, maxWidth: '100%', overflow: 'hidden' }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}>
                    Creates N schedules for sending emails from all available outlook grants. N is the total number of active outlook grants.
                </Typography>
                <TextField id="sched-email" label="Recipient Email" fullWidth size="small" variant="outlined" />
                <TextField id="sched-name" label="Recipient Name" fullWidth size="small" variant="outlined" />
            </Stack>,
            { title: 'Generate Outlook Warmup Schedules', okText: 'Generate Schedules' }
        );

        if (result) {
            const email = (document.getElementById('sched-email') as HTMLInputElement).value;
            const name = (document.getElementById('sched-name') as HTMLInputElement).value;
            if (email && name) {
                outlookScheduleMutation.mutate({ email, name });
            } else {
                enqueueSnackbar("Both Email and Name are required", { variant: 'warning' });
            }
        }
    };
    
    const handleAddAccount = async (type: 'ms' | 'gmail') => {
        const result = await dialogs.confirm(
            <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
                <Typography>Register new {type.toUpperCase()} account:</Typography>
                <TextField id="acc-email" label="Email Address" fullWidth variant="outlined" />
                <TextField id="acc-name" label="Display Name" fullWidth variant="outlined" />
            </Stack>,
            { title: `Add ${type.toUpperCase()} Account`, okText: 'Generate OAuth Link' }
        );

        if (result) {
            const email = (document.getElementById('acc-email') as HTMLInputElement).value;
            const name = (document.getElementById('acc-name') as HTMLInputElement).value;
            
            try {
                const res = await authenticateAndPostData(`/admin/warmup/add-${type}/`, { email, name });
                if (res.data?.auth_url) window.open(res.data.auth_url, "_blank");
            } catch (e) { enqueueSnackbar("Failed to initiate add account", { variant: "error" }); }
        }
    };

    const handleRegisterDomain = async () => {
        let formData = { domain: "", emails: [] as { email: string, name: string }[] };

        const result = await dialogs.confirm(
            <RegisterDomainForm onDataChange={(data) => { formData = data; }} />,
            { 
                title: 'Register New Domain', 
                okText: 'Start Warmup',
                cancelText: 'Cancel'
            }
        );

        if (result) {
            if (!formData.domain || formData.emails.length === 0) {
                enqueueSnackbar("Domain and at least one email are required", { variant: "error" });
                return;
            }
            try {           
                const res = await authenticateAndPostData(`/admin/warmup/register/`, { domain: formData.domain, emails: formData.emails });
                enqueueSnackbar("Domain Registered Successfully", { variant: "success" });
                warmupDomainsQuery.refetch();
                await dialogs.alert(
                    <Box sx={{ width: 400, maxWidth: '100%' }}>
                        <Typography variant="subtitle2" gutterBottom>Server Response:</Typography>
                        <Box component="pre" sx={{ p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: '0.75rem', overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0 }}>
                            {JSON.stringify(res.data, null, 2)}
                        </Box>
                    </Box>,
                    { title: 'Registration Details' }
                );
                } catch (e: any) {
                    enqueueSnackbar(e.data?.message || "Registration failed", { variant: "error" });
                }
            }
        };

    const rowData = useMemo(() => {
        const domains = warmupDomainsQuery.data?.data?.domains || [];
        return domains.map((d: string) => ({ domain: d }));
    }, [warmupDomainsQuery.data]);

    const pauseRowData = useMemo(() => {
        const domains = pauseDomainsQuery.data?.data?.domains || [];
        return domains.map((d: string) => ({ domain: d }));
    }, [pauseDomainsQuery.data]);

    const bulkActionMutation = useMutation({
        mutationFn: async ({ action, domains }: { action: 'pause' | 'resume', domains: string[] }) => 
            authenticateAndPostData(`/admin/warmup/bulk-resume-pause/?action=${action}`, { domains }),
        onSuccess: (_, variables) => {
            enqueueSnackbar(`Bulk ${variables.action} request sent`, { variant: "success" });
            warmupDomainsQuery.refetch();
            pauseDomainsQuery.refetch();
        },
        onError: (err: any) => enqueueSnackbar(err.data?.message || "Bulk action failed", { variant: "error" })
    });

    const handleBulkAction = async (action: 'pause' | 'resume') => {
        
        const targetData = action === 'pause' ? rowData : pauseRowData;

        const allDomains = targetData.map(r => r.domain);
        let selectedDomains: string[] = [];

        if (allDomains.length === 0) {
            enqueueSnackbar("No domains available to process", { variant: "info" });
            return;
        }

        const result = await dialogs.confirm(
            <BulkDomainSelector 
                domains={allDomains} 
                onSelectionChange={(val) => { selectedDomains = val; }} 
            />,
            { 
                title: `Bulk ${action.toUpperCase()}`, 
                okText: `Confirm & Proceed`,
                cancelText: 'Cancel'
            }
        );

        if (result && selectedDomains.length > 0) {
            bulkActionMutation.mutate({ action, domains: selectedDomains });
        } else if (result && selectedDomains.length === 0) {
            enqueueSnackbar("No domains were selected", { variant: "warning" });
        }
    };

    const handleManualDomainAction = async () => {      
        const formValues = { domain: '', action: 'pause' };

        const confirmed = await dialogs.confirm(
            <ManualDomainManagementContent 
                initialDomain="" 
                initialAction="pause"
                onChange={(key, value) => {
                    if (key === 'domain') formValues.domain = value;
                    if (key === 'action') formValues.action = value;
                }} 
            />, 
            { 
                title: "Manual Domain Status Override",
                okText: "Confirm Action",
                cancelText: "Cancel"
            }
        );

        if (confirmed && formValues.domain.trim()) {
            const { domain, action } = formValues;
            
            if (action === 'unregister') {
                const finalCheck = await dialogs.confirm(
                    `Are you sure you want to PERMANENTLY delete ${domain}?`
                );
                if (!finalCheck) return;
            }

            manageStatusMutation.mutate({ 
                domain: domain.trim(), 
                action: action as any 
            });
        }
    };

    const columnDefs: ColDef[] = [
        { field: "domain", headerName: "Domain", flex: 1, filter: true },
        ...(tabValue === 0 ? [{
            headerName: "Live Warmup Status",
            width: 140,
            cellRenderer: (params: any) => {
                const isActive = statusMap[params.data.domain];
                if (isActive === undefined) return (
                    <Typography variant="caption" color="text.disabled">Not Checked</Typography>
                );
                return (
                    <Chip 
                        label={isActive ? "ACTIVE" : "INACTIVE"} 
                        size="small" 
                        color={isActive ? "success" : "error"} 
                    />
                );
            }
        }] : []),
        {
            headerName: "Actions",
            width: 180,
            sortable: false,
            cellRenderer: (params: any) => (
                <Stack direction="row" spacing={1} sx={{ height: '100%' }} alignItems="center">
                    <Tooltip title="Pause Warmup">
                        <IconButton size="small" onClick={() => manageStatusMutation.mutate({ domain: params.data.domain, action: 'pause' })}>
                            <Pause fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Resume Warmup">
                        <IconButton size="small" color="primary" onClick={() => manageStatusMutation.mutate({ domain: params.data.domain, action: 'resume' })}>
                            <PlayArrow fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Unregister Domain (Delete)">
                        <IconButton size="small" color="error" onClick={async () => {
                            if (await dialogs.confirm(`Unregister ${params.data.domain}?`)) 
                                manageStatusMutation.mutate({ domain: params.data.domain, action: 'unregister' });
                        }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ];

    const handleExportWarmupDomains = () => {
        setExportLoading(true);

        try {
            const isActiveTab = tabValue === 0;
            const targetData = isActiveTab ? rowData : pauseRowData;
            const filename = isActiveTab
                ? `active_warmup_domains_${new Date().toISOString().slice(0, 10)}.csv`
                : `paused_warmup_domains_${new Date().toISOString().slice(0, 10)}.csv`;

            // Define columns based on active tab
            const activeColumns: CSVColumn<{ domain: string }>[] = [
                { header: "Domain", accessor: "domain" },
                {
                    header: "Live Warmup Status",
                    accessor: (row) => {
                        const status = statusMap[row.domain];
                        if (status === undefined) return "Not Checked";
                        return status ? "ACTIVE" : "INACTIVE";
                    },
                },
                { header: "Category", accessor: () => "Active" },
            ];

            const pausedColumns: CSVColumn<{ domain: string }>[] = [
                { header: "Domain", accessor: "domain" },
                { header: "Category", accessor: () => "Paused" },
            ];

            const columns = isActiveTab ? activeColumns : pausedColumns;
            const success = exportToCSV(targetData, columns, filename);

            if (success) {
                enqueueSnackbar(
                    `${isActiveTab ? "Active" : "Paused"} warmup domains exported!`,
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar("No domains available to export.", { variant: "error" });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Error while exporting domains.", { variant: "error" });
        } finally {
            setExportLoading(false);
        }
    };
    
    const ExportWarmupDomainsCsvButton = () => {
        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportWarmupDomains}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : `Export ${tabValue === 0 ? "Active" : "Paused"}`}
            </Button>
        );
    };

    if (warmupDomainsQuery.isLoading) return <PageLoading />;
    if (warmupDomainsQuery.error) return <PageDataErrorHandler error={warmupDomainsQuery.error as any} />;

    return (
        <Stack spacing={3} sx={{ p: 3, height: "100%", bgcolor: 'background.default', color: 'text.primary' }}>
            <Box>
                <Typography variant="h5" fontWeight="700">Warmup System</Typography>
                <Typography variant="body2" color="text.secondary">Global Email Warmup Management</Typography>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card variant="outlined" sx={{ flex: 1.5, p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Provisioning & Scheduling</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="contained" size="small" startIcon={<AddLink />} onClick={() => handleAddAccount('gmail')}>Gmail Add</Button>
                        <Button variant="contained" size="small" startIcon={<AddLink />} onClick={() => handleAddAccount('ms')}>MS Add</Button>
                        <Button variant="outlined" color="secondary" size="small" startIcon={<CloudUpload />} onClick={handleRegisterDomain}>Register Domain</Button>
                        <Button variant="outlined" color="info" size="small" startIcon={<ScheduleSend />} onClick={handleGenerateSchedules}>Outlook Schedules</Button>
                        <Button variant="outlined" color="primary" size="small" startIcon={<SettingsSuggest />} onClick={handleManualDomainAction}>Manual Manage (P/R)</Button>
                        <Button variant="outlined" color="warning" size="small" startIcon={<PauseCircleOutline />} onClick={() => handleBulkAction('pause')}>Bulk Pause</Button>
                        <Button variant="outlined" color="success" size="small" startIcon={<PlayCircleOutline />} onClick={() => handleBulkAction('resume')}>Bulk Resume</Button>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{ flex: 1, p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="overline" color="error" sx={{ mb: 1, display: 'block' }}>Account Cleanup</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" color="error" size="small" startIcon={<PersonRemove />} onClick={() => handleDeleteAccount('gmail')}>Del Gmail</Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<PersonRemove />} onClick={() => handleDeleteAccount('ms')}>Del MS</Button>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{ flex: 1, p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Check Active Warmup</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button 
                            variant="contained" color="info" size="small" 
                            startIcon={checkActiveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <FactCheck />}
                            disabled={checkActiveMutation.isPending}
                            onClick={() => checkActiveMutation.mutate(rowData.map(r => r.domain))}
                        >
                            {checkActiveMutation.isPending ? "Syncing..." : "Sync Status"}
                        </Button>
                        <IconButton 
                            onClick={() => tabValue === 0 ? warmupDomainsQuery.refetch() : pauseDomainsQuery.refetch()} 
                            size="small" 
                            sx={{ border: 1, borderColor: 'divider' }}
                        >
                            <Refresh />
                        </IconButton>
                    </Stack>
                </Card>
            </Stack>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
                <Tabs value={tabValue} onChange={handleTabChange} sx={{
                    '& .MuiTabs-flexContainer': {
                        flexWrap: 'wrap',
                    },
                }}>
                    <Tab label="Active Domains" />
                    <Tab label="Pause Domains" />
                </Tabs>
            </Box> 
             <Box sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: '400px',
                    bgcolor: 'background.paper', 
                    p: 1, 
                    borderRadius: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    overflow: 'hidden'
                }}>
                {tabValue === 0 && (
                    <ServerSideDataTable rows={rowData} columns={columnDefs} actions={<ExportWarmupDomainsCsvButton />}/>
                )}
                {tabValue === 1 && (
                    <ServerSideDataTable rows={pauseRowData} columns={columnDefs} actions={<ExportWarmupDomainsCsvButton />}/>
                )}
            </Box>
        </Stack>
    );
}


function RegisterDomainForm({ 
  onDataChange 
}: { 
  onDataChange: (data: { domain: string, emails: { email: string, name: string }[] }) => void 
}) {
    const [domain, setDomain] = useState("");
    const [emails, setEmails] = useState([{ email: "", name: "" }]);

    useEffect(() => {
        onDataChange({ domain, emails: emails.filter(e => e.email.trim() !== "") });
    }, [domain, emails]);

    const addEmailRow = () => setEmails([...emails, { email: "", name: "" }]);
    
    const removeEmailRow = (index: number) => {
        if (emails.length > 1) setEmails(emails.filter((_, i) => i !== index));
    };

    const handleEmailChange = (index: number, field: 'email' | 'name', value: string) => {
        const newEmails = [...emails];
        newEmails[index][field] = value;
        setEmails(newEmails);
    };

    return (
        <Stack 
            spacing={2} 
            sx={{ 
                mt: 1, 
                width: 450,
                maxWidth: '100%',
                overflow: 'hidden', 
                p: 0.5,
                boxSizing: 'border-box' 
            }}
        >
            <TextField 
                label="Domain (e.g. apple.com)" 
                fullWidth 
                size="small"
                value={domain} 
                onChange={(e) => setDomain(e.target.value)} 
            />
            
            <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">Warmup Users</Typography>
            </Divider>
            
            <Box 
                sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    overflowX: 'hidden', 
                    pr: 0.5,
                    // Modern theme-aware scrollbar
                    '&::-webkit-scrollbar': { width: '5px' },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { 
                        bgcolor: 'divider', 
                        borderRadius: '10px',
                        '&:hover': { bgcolor: 'text.disabled' }
                    }
                }}
            >
                {emails.map((row, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <TextField 
                            label="Email" 
                            size="small"
                            sx={{ flex: 2 }}
                            value={row.email} 
                            onChange={(e) => handleEmailChange(index, 'email', e.target.value)} 
                        />
                        <TextField 
                            label="Name" 
                            size="small"
                            sx={{ flex: 1 }}
                            value={row.name} 
                            onChange={(e) => handleEmailChange(index, 'name', e.target.value)} 
                        />
                        <IconButton 
                            color="error" 
                            size="small"
                            disabled={emails.length === 1} 
                            onClick={() => removeEmailRow(index)}
                        >
                            <RemoveCircleOutline fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
            </Box>

            <Button 
                startIcon={<Add />} 
                onClick={addEmailRow} 
                variant="outlined" 
                size="small" 
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
                Add User
            </Button>
        </Stack>
    );
}

function BulkDomainSelector({ domains, onSelectionChange }: any) {
    const theme = useTheme();
    const [selected, setSelected] = useState<string[]>(domains);
    useEffect(() => onSelectionChange(selected), [selected]);

    return (
        <Stack spacing={2} sx={{ width: 550, maxWidth: '100%', mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">Select domains:</Typography>
                <Button size="small" onClick={() => setSelected(selected.length === domains.length ? [] : domains)}>
                    {selected.length === domains.length ? "Deselect All" : "Select All"}
                </Button>
            </Stack>
            <Box sx={{ maxHeight: 350, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                {domains.map((domain: any) => (
                    <Box key={domain} onClick={() => setSelected(prev => prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain])}
                        sx={{ p: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Checkbox size="small" checked={selected.includes(domain)} sx={{ p: 0, mr: 1.5 }} />
                        <Typography variant="body2">{domain}</Typography>
                    </Box>
                ))}
            </Box>
            
            <Stack direction="row" justifyContent="flex-end">
                <Typography 
                    variant="caption" 
                    sx={{ 
                        fontWeight: 700, 
                        // This creates a subtle primary tint that works in both light and dark mode
                        bgcolor: alpha(theme.palette.primary.main, 0.1), 
                        color: 'primary.main',
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1 
                    }}
                >
                    {selected.length} domains selected
                </Typography>
            </Stack>
        </Stack>
    );
}

function ManualDomainManagementContent({ 
    initialDomain,
    initialAction,
    onChange 
}: { 
    initialDomain: string,
    initialAction: string,
    onChange: (key: string, value: string) => void 
}) {
    const [localDomain, setLocalDomain] = useState(initialDomain);
    const [localAction, setLocalAction] = useState(initialAction);

    useEffect(() => {
        onChange('domain', localDomain);
    }, [localDomain]);

    useEffect(() => {
        onChange('action', localAction);
    }, [localAction]);

    return (
        <Stack spacing={2.5} sx={{ pt: 1, minWidth: 350 }}>
            <Typography variant="body2" color="text.secondary">
                Enter the domain name and the action to apply.
            </Typography>

            <TextField
                fullWidth
                label="Domain Name"
                placeholder="e.g. example.com"
                variant="outlined"
                size="small"
                autoFocus
                value={localDomain}
                onChange={(e) => setLocalDomain(e.target.value)}            
                onKeyDown={(e) => e.stopPropagation()} 
            />

            <FormControl fullWidth size="small">
                <InputLabel id="manual-action-label">Action</InputLabel>
                <Select
                    labelId="manual-action-label"
                    value={localAction}
                    label="Action"
                    onChange={(e) => setLocalAction(e.target.value)}
                >
                    <MenuItem value="pause">Pause Warmup</MenuItem>
                    <MenuItem value="resume">Resume Warmup</MenuItem>
                    <MenuItem value="unregister" sx={{ color: 'error.main' }}>Unregister (Delete)</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );
}